import React from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Badge } from '@/components/ui/badge';
import { DateTimePicker } from '@/components/ui/datetime-picker';
import {
  ShoppingCart,
  Search,
  Plus,
  Minus,
  Trash2,
  Package,
  User,
  CreditCard,
  FileText,
  Building2,
  Receipt,
} from 'lucide-react';
import { Textarea } from '@/components/ui/textarea';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';

interface CreateLayawayTabProps {
  // Items and inventory
  filteredAndSortedItems: any[];
  itemSearchTerm: string;
  setItemSearchTerm: (value: string) => void;
  sortBy: string;
  setSortBy: (value: string) => void;

  // Cart
  cart: any[];
  addToCart: (item: any) => void;
  removeFromCart: (id: string) => void;
  updateCartQuantity: (id: string, quantity: number) => void;
  updateCartItemPrice: (id: string, price: number) => void;

  // Customer
  customer: { name: string; phone: string; address: string };
  setCustomer: (customer: any) => void;

  // Payment
  productHandling: 'hold' | 'handover';
  setProductHandling: (value: 'hold' | 'handover') => void;
  paymentScheduleMonths: number;
  setPaymentScheduleMonths: (value: number) => void;
  initialPayment: number;
  setInitialPayment: (value: number) => void;
  paymentMethod: string;
  setPaymentMethod: (value: string) => void;
  notes: string;
  setNotes: (value: string) => void;
  layawayDate: Date;
  setLayawayDate: (value: Date) => void;
  systemTimezone: string;

  // Branches
  isAdmin: boolean;
  selectedBranch: string;
  setSelectedBranch: (value: string) => void;
  branches: any[];

  // Totals
  cartSubtotal: number;
  cartVAT: number;
  cartTotal: number;
  vatPercentage: number;
  currencySymbol: string;

  // Actions
  createLayaway: () => void;
  isCreating: boolean;
}

export const CreateLayawayTab: React.FC<CreateLayawayTabProps> = ({
  filteredAndSortedItems,
  itemSearchTerm,
  setItemSearchTerm,
  sortBy,
  setSortBy,
  cart,
  addToCart,
  removeFromCart,
  updateCartQuantity,
  updateCartItemPrice,
  customer,
  setCustomer,
  productHandling,
  setProductHandling,
  paymentScheduleMonths,
  setPaymentScheduleMonths,
  initialPayment,
  setInitialPayment,
  paymentMethod,
  setPaymentMethod,
  notes,
  setNotes,
  systemTimezone,
  layawayDate,
  setLayawayDate,
  isAdmin,
  selectedBranch,
  setSelectedBranch,
  branches,
  cartSubtotal,
  cartVAT,
  cartTotal,
  vatPercentage,
  currencySymbol,
  createLayaway,
  isCreating,
}) => {
  const remainingBalance = Math.max(0, cartTotal - initialPayment);
  const monthlyPayment = paymentScheduleMonths > 0 ? remainingBalance / paymentScheduleMonths : 0;

  return (
    <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
      {/* Left Side - Items Selection */}
      <div className="space-y-6">
        <Card>
          <CardHeader>
            <div className="flex items-center justify-between">
              <CardTitle className="flex items-center gap-2">
                <Package className="h-5 w-5" />
                Select Items
              </CardTitle>
              <Badge variant="secondary">
                {filteredAndSortedItems.length} items
              </Badge>
            </div>
            
            {/* Search and Sort */}
            <div className="flex gap-2 mt-4">
              <div className="relative flex-1">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
                <Input
                  placeholder="Search items..."
                  value={itemSearchTerm}
                  onChange={(e) => setItemSearchTerm(e.target.value)}
                  className="pl-10"
                />
              </div>
              <Select value={sortBy} onValueChange={setSortBy}>
                <SelectTrigger className="w-32">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="name">Name</SelectItem>
                  <SelectItem value="price">Price</SelectItem>
                  <SelectItem value="stock">Stock</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </CardHeader>
          
          <CardContent className="max-h-[600px] overflow-y-auto">
            <div className="space-y-2">
              {filteredAndSortedItems.map((item) => (
                <div
                  key={item.id}
                  className="flex items-center justify-between p-3 border rounded-lg hover:bg-gray-50"
                >
                  <div className="flex-1">
                    <div className="font-medium">{item.name}</div>
                    <div className="text-sm text-gray-500">
                      SKU: {item.sku} | {currencySymbol}{parseFloat(item.selling_price.toString()).toFixed(2)}
                      {item.vat_enabled && <Badge variant="outline" className="ml-2 text-xs">VAT</Badge>}
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    <Badge variant={item.availableQuantity > 0 ? 'default' : 'destructive'}>
                      {item.availableQuantity} in stock
                    </Badge>
                    <Button
                      size="sm"
                      onClick={() => addToCart(item)}
                      disabled={item.availableQuantity === 0}
                    >
                      <Plus className="h-4 w-4" />
                    </Button>
                  </div>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Right Side - Order Details */}
      <div className="space-y-6">
        {/* Branch Selection for Admin */}
        {isAdmin && (
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Building2 className="h-5 w-5" />
                Branch Selection
              </CardTitle>
            </CardHeader>
            <CardContent>
              <Select value={selectedBranch} onValueChange={setSelectedBranch}>
                <SelectTrigger>
                  <SelectValue placeholder="Select a branch" />
                </SelectTrigger>
                <SelectContent>
                  {branches.map((branch) => (
                    <SelectItem key={branch.id} value={branch.id}>
                      {branch.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </CardContent>
          </Card>
        )}

        {/* Current Order */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <ShoppingCart className="h-5 w-5" />
              Current Order
            </CardTitle>
          </CardHeader>
          <CardContent>
            {cart.length === 0 ? (
              <div className="text-center py-8 text-gray-500">
                No items in order
              </div>
            ) : (
              <div className="space-y-2">
                {cart.map((item) => (
                  <div key={item.id} className="flex items-center justify-between p-2 border rounded">
                    <div className="flex-1">
                      <div className="font-medium text-sm">{item.name}</div>
                      <div className="flex items-center gap-2 mt-1">
                        <Input
                          type="number"
                          step="0.01"
                          value={parseFloat(item.price.toString()).toFixed(2)}
                          onChange={(e) => updateCartItemPrice(item.id, parseFloat(e.target.value) || 0)}
                          className="w-20 h-6 text-xs"
                        />
                        <span className="text-xs text-gray-500">each</span>
                      </div>
                    </div>
                    <div className="flex items-center gap-2">
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => updateCartQuantity(item.id, item.quantity - 1)}
                        className="h-6 w-6 p-0"
                      >
                        <Minus className="h-3 w-3" />
                      </Button>
                      <span className="w-8 text-center text-sm">{item.quantity}</span>
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => updateCartQuantity(item.id, item.quantity + 1)}
                        className="h-6 w-6 p-0"
                      >
                        <Plus className="h-3 w-3" />
                      </Button>
                      <Button
                        size="sm"
                        variant="ghost"
                        onClick={() => removeFromCart(item.id)}
                        className="h-6 w-6 p-0 text-red-500"
                      >
                        <Trash2 className="h-3 w-3" />
                      </Button>
                    </div>
                  </div>
                ))}
                
                {/* Totals */}
                <div className="border-t pt-3 mt-3 space-y-1">
                  <div className="flex justify-between text-sm">
                    <span>Subtotal:</span>
                    <span>{currencySymbol}{cartSubtotal.toFixed(2)}</span>
                  </div>
                  {cartVAT > 0 && (
                    <div className="flex justify-between text-sm">
                      <span>VAT ({vatPercentage}%):</span>
                      <span>{currencySymbol}{cartVAT.toFixed(2)}</span>
                    </div>
                  )}
                  <div className="flex justify-between font-bold">
                    <span>Total:</span>
                    <span>{currencySymbol}{cartTotal.toFixed(2)}</span>
                  </div>
                </div>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Customer Details */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <User className="h-5 w-5" />
              Customer Details
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div>
              <Label>Name *</Label>
              <Input
                value={customer.name}
                onChange={(e) => setCustomer({ ...customer, name: e.target.value })}
                placeholder="Customer name"
              />
            </div>
            <div>
              <Label>Phone</Label>
              <Input
                value={customer.phone}
                onChange={(e) => setCustomer({ ...customer, phone: e.target.value })}
                placeholder="Phone number"
              />
            </div>
            <div>
              <Label>Address</Label>
              <Input
                value={customer.address}
                onChange={(e) => setCustomer({ ...customer, address: e.target.value })}
                placeholder="Address"
              />
            </div>
          </CardContent>
        </Card>

        {/* Payment Details */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <CreditCard className="h-5 w-5" />
              Payment Details
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div>
              <Label>Layaway Date</Label>
              <DateTimePicker
                value={layawayDate}
                onChange={(date) => date && setLayawayDate(date)}
                timezone={systemTimezone}
                showTime={false}
              />
            </div>

            <div>
              <Label>Product Handling</Label>
              <RadioGroup value={productHandling} onValueChange={(value: any) => setProductHandling(value)}>
                <div className="flex items-center space-x-2">
                  <RadioGroupItem value="hold" id="hold" />
                  <Label htmlFor="hold">Hold Product (Reserve in store)</Label>
                </div>
                <div className="flex items-center space-x-2">
                  <RadioGroupItem value="handover" id="handover" />
                  <Label htmlFor="handover">Handover Product (Customer takes now)</Label>
                </div>
              </RadioGroup>
            </div>
            
            <div>
              <Label>Payment Schedule (Months)</Label>
              <Select value={paymentScheduleMonths.toString()} onValueChange={(v) => setPaymentScheduleMonths(parseInt(v))}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {[1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12].map(m => (
                    <SelectItem key={m} value={m.toString()}>{m} month{m > 1 ? 's' : ''}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            
            <div>
              <Label>Initial Payment</Label>
              <Input
                type="number"
                step="0.01"
                value={initialPayment}
                onChange={(e) => setInitialPayment(parseFloat(e.target.value) || 0)}
                placeholder="0.00"
              />
            </div>
            
            <div>
              <Label>Payment Method</Label>
              <Select value={paymentMethod} onValueChange={setPaymentMethod}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="cash">Cash</SelectItem>
                  <SelectItem value="card">Card</SelectItem>
                  <SelectItem value="transfer">Transfer</SelectItem>
                  <SelectItem value="mobile">Mobile Money</SelectItem>
                </SelectContent>
              </Select>
            </div>
            
            <div>
              <Label>Notes</Label>
              <Textarea
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
                placeholder="Optional notes..."
                rows={3}
              />
            </div>
            
            {/* Summary */}
            {cart.length > 0 && customer.name && (
              <div className="bg-blue-50 rounded-lg p-3 space-y-1">
                <div className="font-semibold text-blue-900 mb-2">Summary</div>
                <div className="text-sm text-blue-800 grid grid-cols-2 gap-2">
                  <div>Total Amount:</div>
                  <div className="font-medium">{currencySymbol}{cartTotal.toFixed(2)}</div>
                  <div>Initial Payment:</div>
                  <div className="font-medium">{currencySymbol}{initialPayment.toFixed(2)}</div>
                  <div>Remaining:</div>
                  <div className="font-medium">{currencySymbol}{remainingBalance.toFixed(2)}</div>
                  <div>Monthly Payment:</div>
                  <div className="font-medium">{currencySymbol}{monthlyPayment.toFixed(2)}</div>
                </div>
              </div>
            )}
            
            <Button
              className="w-full"
              onClick={createLayaway}
              disabled={cart.length === 0 || !customer.name.trim() || isCreating || initialPayment > cartTotal}
            >
              {isCreating ? 'Creating...' : 'Create Layaway'}
            </Button>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};
